import { ChangeEvent, useEffect, useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json as jsonLanguage } from "@codemirror/lang-json";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { parseSyntheticKlvTemplateDocument } from "../syntheticKlvTemplate.mjs";

type AuthorizedFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type ValidationFinding = {
  rule_id?: string;
  severity?: string;
  message?: string;
  path?: string;
  tag?: number;
  field_name?: string;
};

type ValidationResult = {
  valid?: boolean;
  revision?: number;
  error?: string;
  packet?: unknown;
  report?: { findings?: ValidationFinding[] };
};

type Props = {
  readOnly?: boolean;
 validationEndpoint?:string;purpose?:"pattern" | "flywheel";applyEndpoint?:string;processorId?:string;applyLabel?:string;
  open: boolean;
  value: string;
  authorizedFetch: AuthorizedFetch;
  onClose: () => void;
  onSave: (value: string) => void;
};

const findingColor = (severity?: string): "default" | "warning" | "error" => {
  if (severity === "error") return "error";
  if (severity === "warning") return "warning";
  return "default";
};

export default function SyntheticKlvTemplateDialog({ open, value, authorizedFetch, onClose, onSave, validationEndpoint="/api/flywheel/synthetic-klv/validate", purpose="flywheel", applyEndpoint, processorId, applyLabel="Save", readOnly = false }: Props) {
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const theme = useTheme();
  const [draft, setDraft] = useState(value);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [localError, setLocalError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState(false);
  const validationSequence = useRef(0);
  const importInput = useRef<HTMLInputElement | null>(null);

  const validateTemplate = async (fieldsJson: string, revision: number): Promise<ValidationResult> => {
    const response = await authorizedFetch(validationEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ syntheticKlvFieldsJson: fieldsJson, syntheticKlvRevision: revision })
    });
    const payload = await response.json().catch(() => null) as (ValidationResult & { error?: string }) | null;
    if (!response.ok) throw new Error(payload?.error || "Synthetic KLV validation failed.");
    return payload ?? { valid: false, error: "The server returned an empty validation result." };
  };

  useEffect(() => {
    if (!open) {
      validationSequence.current += 1;
      setBusy(false);
      return;
    }
    setDraft(value);
    setValidation(null);
    setLocalError("");
    setActionError("");
  // A successful persistence followed by failed runtime application updates
  // the parent value. Keep this editor and its explicit failure visible.
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const sequence = validationSequence.current + 1;
    validationSequence.current = sequence;
    let parsed;
    try {
      parsed = parseSyntheticKlvTemplateDocument(draft);
      setLocalError("");
      setValidation(null);
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : "Invalid template JSON.");
      setValidation(null);
      setBusy(false);
      return undefined;
    }
    if (readOnly) {
      setBusy(false);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      setBusy(true);
      try {
        const result = await validateTemplate(parsed.fieldsJson, parsed.revision);
        if (validationSequence.current === sequence) {
          setValidation(result);
          setActionError("");
        }
      } catch (error) {
        if (validationSequence.current === sequence) {
          setValidation(null);
          setActionError(error instanceof Error ? error.message : "Synthetic KLV validation failed.");
        }
      } finally {
        if (validationSequence.current === sequence) setBusy(false);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [draft, open, readOnly]);

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    if (readOnlyRef.current) return;
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setActionError("");
      const contents = await file.text();
      if (!readOnlyRef.current) setDraft(contents);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Import failed.");
    }
  };

  const handleFormat = () => {
    if (readOnlyRef.current) return;
    try {
      setDraft(parseSyntheticKlvTemplateDocument(draft).canonicalJson);
      setActionError("");
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "The JSON cannot be formatted.");
    }
  };

  const handleExport = () => {
    try {
      const canonical = `${parseSyntheticKlvTemplateDocument(draft).canonicalJson}\n`;
      const url = URL.createObjectURL(new Blob([canonical], { type: "application/json" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "synthetic-klv-template.json";
      link.click();
      URL.revokeObjectURL(url);
      setActionError("");
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Export failed.");
    }
  };

  const handleSave = async () => {
    if (readOnlyRef.current) return;
    setBusy(true);
    setActionError("");
    try {
      const parsed = parseSyntheticKlvTemplateDocument(draft);
      const result = await validateTemplate(parsed.fieldsJson, parsed.revision);
      if (readOnlyRef.current) return;
      setValidation(result);
      if (!result.valid) return;
      if (applyEndpoint) {
        const response=await authorizedFetch(applyEndpoint,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({processorId,syntheticKlvFieldsJson:parsed.fieldsJson,syntheticKlvRevision:parsed.revision})});
        const result=await response.json();
        if (!response.ok || !result.saved) throw new Error(result.error || "Template was not saved.");
        onSave(parsed.canonicalJson);
        if (!result.applied && applyLabel==="Save & apply") {setActionError(`Saved, but not applied: ${result.error || "worker unavailable"}`);return;}
      } else {onSave(parsed.canonicalJson);}
      onClose();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Failed to save the synthetic KLV template.");
    } finally {
      setBusy(false);
    }
  };

  const findings = Array.isArray(validation?.report?.findings) ? validation.report.findings : [];
  const saveEnabled = !busy && !localError && Boolean(validation?.valid);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.25}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="h6">Synthetic KLV fields</Typography>
            <Stack direction="row" gap={0.5} sx={{ ml: "auto", flexShrink: 0 }}>
              <input ref={importInput} hidden type="file" accept="application/json,.json" onChange={handleImport} />
              {!readOnly ? <Tooltip title="Import JSON"><IconButton aria-label="Import synthetic KLV JSON" onClick={() => importInput.current?.click()} disabled={busy}><UploadIcon /></IconButton></Tooltip> : null}
              <Tooltip title="Export valid JSON"><span><IconButton aria-label="Export synthetic KLV JSON" onClick={handleExport} disabled={readOnly ? Boolean(localError) : !saveEnabled}><DownloadIcon /></IconButton></span></Tooltip>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary">{readOnly ? "Read-only synthetic KLV template." : purpose==="pattern"?"Edit the MISB ST 0601 fields generated by the Pattern source.":"Edit the exact MISB ST 0601 fields inserted when source KLV is unavailable."}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Chip size="small" label="Tag 1 · generated checksum" variant="outlined" />
            <Chip size="small" label="Tag 2 · generated timestamp" variant="outlined" />
            <Chip size="small" label={readOnly ? "Tag 65 · revision" : "Tag 65 · editable revision"} color="primary" variant="outlined" />
            {busy ? <CircularProgress size={18} /> : null}
            {!busy && validation?.valid ? <Chip size="small" label="Valid packet" color="success" /> : null}
            {!busy && (localError || validation?.valid === false) ? <Chip size="small" label="Needs attention" color="error" /> : null}
          </Stack>
          {localError || actionError || validation?.error ? <Alert severity="error">{localError || actionError || validation?.error}</Alert> : null}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: readOnly ? "1fr" : "minmax(0, 1.1fr) minmax(320px, 0.9fr)" }, gap: 2 }}>
            <Paper variant="outlined" sx={{ overflow: "hidden", minWidth: 0 }}>
              <Box sx={{ px: 1.5, py: 0.5, bgcolor: "action.hover", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="subtitle2">Template JSON</Typography>
                {!readOnly ? <Tooltip title="Format JSON"><IconButton size="small" aria-label="Format synthetic KLV JSON" onClick={handleFormat} sx={{ color: "inherit" }}><DataObjectIcon fontSize="small" /></IconButton></Tooltip> : null}
              </Box>
              <Divider />
              <CodeMirror value={draft} readOnly={readOnly} editable={!readOnly} height="410px" theme={theme.palette.mode} extensions={[jsonLanguage()]} onChange={value => { if (!readOnlyRef.current) setDraft(value); }} basicSetup={{ foldGutter: true, highlightActiveLine: true }} aria-label="Synthetic KLV template JSON editor" />
            </Paper>
            {!readOnly ? <Paper variant="outlined" sx={{ overflow: "hidden", minWidth: 0 }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: "action.hover" }}><Typography variant="subtitle2">Generated packet preview</Typography></Box>
              <Divider />
              <Box component="pre" sx={{ m: 0, p: 1.5, height: 410, overflow: "auto", fontSize: 12, lineHeight: 1.55, fontFamily: "monospace" }}>
                {validation?.packet ? JSON.stringify(validation.packet, null, 2) : "Packet preview appears after validation."}
              </Box>
            </Paper> : null}
          </Box>
          {findings.length > 0 ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Validation findings</Typography>
              <Stack spacing={1}>
                {findings.map((finding, index) => (
                  <Stack key={`${finding.rule_id || "finding"}-${finding.path || index}-${index}`} direction="row" gap={1} alignItems="flex-start">
                    <Chip size="small" color={findingColor(finding.severity)} label={finding.severity || "info"} />
                    <Box>
                      <Typography variant="body2">{finding.message || "Validation finding"}</Typography>
                      {finding.path || finding.tag != null || finding.field_name ? <Typography variant="caption" color="text.secondary">{finding.path ? `Tag path ${finding.path}` : finding.tag != null ? `Tag ${finding.tag}` : finding.field_name}</Typography> : null}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>{readOnly ? "Close" : "Cancel"}</Button>
        {!readOnly ? <Button variant="contained" onClick={handleSave} disabled={!saveEnabled}>{applyEndpoint ? applyLabel : "Save"}</Button> : null}
      </DialogActions>
    </Dialog>
  );
}
